import { useState, useMemo, useCallback } from "react";

const MAX_VISIBLE_CHATS = 3;

const useChatList = ({ maxVisible = MAX_VISIBLE_CHATS } = {}) => {
    const [chats, setChats] = useState([]);
    const [activeChatIds, setActiveChatIds] = useState([]);

    const visibleChats = useMemo(() => {
        const chatMap = new Map(chats.map(chat => [chat.id, chat]));
        return activeChatIds.map(id => chatMap.get(id)).filter(Boolean);
    }, [chats, activeChatIds]);

    const hiddenChats = useMemo(() => {
        const activeIds = new Set(activeChatIds);
        return chats.filter(chat => !activeIds.has(chat.id));
    }, [chats, activeChatIds]);

    const addChat = useCallback((chat) => {
        setChats(prev => [...prev, chat]);
    }, []);

    const removeChat = useCallback((chatId) => {
        setChats(prev => prev.filter(chat => chat.id !== chatId));
        setActiveChatIds(prev => prev.filter(id => id !== chatId));
    }, []);

    const appendMessage = useCallback((chatId, message) => {
        setChats(prev => prev.map(chat =>
            chat.id === chatId
                ? { ...chat, messages: [...chat.messages, message] }
                : chat
        ));
    }, []);

    const popLastMessage = useCallback((chatId) => {
        setChats(prev => prev.map(chat =>
            chat.id === chatId
                ? { ...chat, messages: chat.messages.slice(0, -1) }
                : chat
        ));
    }, []);

    const setBotTyping = useCallback((chatId, typing) => {
        setChats(prev => prev.map(chat =>
            chat.id === chatId ? { ...chat, isBotTyping: typing } : chat
        ));
    }, []);

    const mergeEntities = useCallback((chatId, entities) => {
        setChats(prev => prev.map(chat =>
            chat.id === chatId
                ? { ...chat, entities: { ...(chat.entities || {}), ...entities } }
                : chat
        ));
    }, []);

    const activateChat = useCallback((chatId) => {
        setActiveChatIds(prev => {
            const newActiveIds = [chatId, ...prev.filter(i => i !== chatId)];
            return newActiveIds.slice(0, maxVisible);
        });
    }, [maxVisible]);

    const deactivateChat = useCallback((chatId) => {
        setActiveChatIds(prev => prev.filter(id => id !== chatId));
    }, []);

    const setConversationId = useCallback((chatId, conversationId) => {
        setChats(prev => prev.map(chat =>
            chat.id === chatId ? { ...chat, conversationId } : chat
        ));
    }, []);

    return {
        chats,
        activeChatIds,
        visibleChats,
        hiddenChats,
        addChat,
        removeChat,
        appendMessage,
        popLastMessage,
        setBotTyping,
        mergeEntities,
        activateChat,
        deactivateChat,
        setConversationId,
    };
};

export default useChatList;
